import * as THREE from "three";
import RAPIER from "@dimforge/rapier3d-compat";

import { SceneManager } from "../../../core/scene_manager.js";
import { PhysicsWorld } from "../../../physics/physics_world.js";
import { ResourceManager } from "../../../resource_loader/resource_manager.js";
import { ResourceID } from "../../../resource_loader/resource_id.js";

interface PlaneFrame
{
  min_x: number;
  max_x: number;
  max_y: number;
}

export interface WallSolidSpan
{
  belt_back_z: number;
  opening_top_y: number;
}

export class EscalatorWalls
{
  private static readonly ART_PX_W = 739;
  private static readonly ART_PX_H = 1977;
  private static readonly ART_BASE_TOP_PX = 1340;
  private static readonly BASE_TOP_SHARE = 1 - EscalatorWalls.ART_BASE_TOP_PX / EscalatorWalls.ART_PX_H;
  private static readonly BASE_ABOVE_BELT_CUBES = 1.6;
  private static readonly EDGE_MARGIN = 0.5;
  private static readonly CORNERS = [ [ -1, -1 ], [ 1, -1 ], [ -1, 1 ], [ 1, 1 ] ];

  private static readonly SOLID_REACH = 30;
  private static readonly SOLID_TOP_Y = 40;
  private static readonly SOLID_BOTTOM_Y = -1;
  private static readonly PLATE_THICKNESS = 0.2;
  private static readonly BELT_BACK_GAP = 0.03;

  private readonly meshes: THREE.Mesh[] = [];
  private readonly material: THREE.MeshBasicMaterial;
  private readonly panel_w: number;
  private readonly panel_h: number;
  private readonly solid: RAPIER.RigidBody;

  constructor(
    inner_left_x: number,
    inner_right_x: number,
    surface_y: number,
    wall_z: number,
    cube_size: THREE.Vector3,
    span: WallSolidSpan,
    pocket_on_right: boolean = false
  )
  {
    const texture = EscalatorWalls.prepareTexture();
    const base_top_y = surface_y + cube_size.y * EscalatorWalls.BASE_ABOVE_BELT_CUBES;

    this.panel_h = base_top_y / EscalatorWalls.BASE_TOP_SHARE;
    this.panel_w = this.panel_h * EscalatorWalls.ART_PX_W / EscalatorWalls.ART_PX_H;
    this.material = new THREE.MeshBasicMaterial( { map: texture, transparent: true, alphaTest: 0.5, fog: false } );

    const frame = EscalatorWalls.frameOnPlane( wall_z );
    const outer_left_x = frame.min_x - EscalatorWalls.EDGE_MARGIN;
    const outer_right_x = frame.max_x + EscalatorWalls.EDGE_MARGIN;
    const top_y = frame.max_y + EscalatorWalls.EDGE_MARGIN;

    if ( inner_left_x > outer_left_x )
      this.addWall( outer_left_x, inner_left_x, inner_left_x, top_y, wall_z );

    if ( outer_right_x > inner_right_x )
      this.addWall( inner_right_x, outer_right_x, inner_right_x, top_y, wall_z );

    this.solid = PhysicsWorld.getWorld().createRigidBody( RAPIER.RigidBodyDesc.fixed() );
    this.addSolids( inner_left_x, inner_right_x, wall_z, span, pocket_on_right );
  }

  public dispose(): void
  {
    PhysicsWorld.getWorld().removeRigidBody( this.solid );

    for ( const mesh of this.meshes )
    {
      SceneManager.world.remove( mesh );
      mesh.geometry.dispose();
    }

    this.meshes.length = 0;
    this.material.dispose();
  }

  private static prepareTexture(): THREE.Texture
  {
    const texture = ResourceManager.getTexture( ResourceID.EscalatorWallTexture );

    texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.needsUpdate = true;

    return texture;
  }

  private static frameOnPlane( z: number ): PlaneFrame
  {
    const camera = SceneManager.camera;
    camera.updateMatrixWorld( true );

    const origin = camera.position;
    const ray = new THREE.Vector3();
    const frame: PlaneFrame = { min_x: Infinity, max_x: -Infinity, max_y: -Infinity };

    for ( const [ nx, ny ] of EscalatorWalls.CORNERS )
    {
      ray.set( nx, ny, 0.5 ).unproject( camera ).sub( origin );

      const t = ( z - origin.z ) / ray.z;
      const x = origin.x + ray.x * t;
      const y = origin.y + ray.y * t;

      frame.min_x = Math.min( frame.min_x, x );
      frame.max_x = Math.max( frame.max_x, x );
      frame.max_y = Math.max( frame.max_y, y );
    }

    return frame;
  }

  private addSolids( inner_left_x: number, inner_right_x: number, wall_z: number, span: WallSolidSpan, pocket_on_right: boolean ): void
  {
    const reach = EscalatorWalls.SOLID_REACH;
    const top_y = EscalatorWalls.SOLID_TOP_Y;
    const bottom_y = EscalatorWalls.SOLID_BOTTOM_Y;
    const plate = EscalatorWalls.PLATE_THICKNESS;
    const front_z = wall_z + plate;
    const back_inner_z = span.belt_back_z - EscalatorWalls.BELT_BACK_GAP;
    const back_z = back_inner_z - plate;
    const opening_y = span.opening_top_y;
    const pocket_min_x = pocket_on_right ? inner_right_x : inner_left_x - reach;
    const pocket_max_x = pocket_on_right ? inner_right_x + reach : inner_left_x;
    const plain_min_x = pocket_on_right ? inner_left_x - reach : inner_right_x;
    const plain_max_x = pocket_on_right ? inner_left_x : inner_right_x + reach;

    this.addSolid( new THREE.Vector3( pocket_min_x, opening_y, back_z ), new THREE.Vector3( pocket_max_x, top_y, front_z ) );
    this.addSolid( new THREE.Vector3( pocket_min_x, bottom_y, wall_z ), new THREE.Vector3( pocket_max_x, opening_y, front_z ) );
    this.addSolid( new THREE.Vector3( pocket_min_x, bottom_y, back_z ), new THREE.Vector3( pocket_max_x, opening_y, back_inner_z ) );
    this.addSolid( new THREE.Vector3( plain_min_x, bottom_y, back_z ), new THREE.Vector3( plain_max_x, top_y, front_z ) );
  }

  private addSolid( min: THREE.Vector3, max: THREE.Vector3 ): void
  {
    const half = max.clone().sub( min ).multiplyScalar( 0.5 );
    const center = min.clone().add( half );

    PhysicsWorld.getWorld().createCollider(
      RAPIER.ColliderDesc.cuboid( half.x, half.y, half.z ).setTranslation( center.x, center.y, center.z ),
      this.solid
    );
  }

  private addWall( x0: number, x1: number, inner_x: number, top_y: number, z: number ): void
  {
    const width = x1 - x0;
    const center_x = x0 + width * 0.5;
    const center_y = top_y * 0.5;
    const geometry = new THREE.PlaneGeometry( width, top_y );
    const position = geometry.attributes.position;
    const uv = geometry.attributes.uv;

    for ( let i = 0; i < position.count; i++ )
    {
      const x = center_x + position.getX( i );
      const y = center_y + position.getY( i );
      const u = 1 - Math.abs( x - inner_x ) / this.panel_w;
      const v = 1 - y / this.panel_h;

      uv.setXY( i, u, v );
    }

    uv.needsUpdate = true;

    const mesh = new THREE.Mesh( geometry, this.material );
    mesh.position.set( center_x, center_y, z );
    SceneManager.world.add( mesh );
    this.meshes.push( mesh );
  }
}
